import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { uk } from 'date-fns/locale';
import { discountsApi } from '../api/discounts';
import { usageApi } from '../api/usage';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/ui/Button';
import DiscountUsageModal from '../components/discounts/DiscountUsageModal';
import RichTextDisplay from '../components/ui/RichTextDisplay';
import {
  Calendar,
  Store,
  MapPin,
  Phone,
  Mail,
  Clock,
  Percent,
  CreditCard,
  AlertCircle,
  ArrowLeft,
  Tag,
  Users,
  Check
} from 'lucide-react';

const DiscountDetails = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [showUsageModal, setShowUsageModal] = useState(false);

  const { data: discount, isLoading, isError } = useQuery({
    queryKey: ['discount', id],
    queryFn: () => discountsApi.getDiscount(id).then(res => res.data),
    enabled: !!id,
  });

  const { data: usageCheckData } = useQuery({
    queryKey: ['usage-check', user?.id, id],
    queryFn: () => usageApi.checkUsage(user.id, id).then(res => res.data),
    enabled: !!user?.id && !!id,
    staleTime: 60 * 1000,
  });

  const hasUsedDiscount = usageCheckData?.used === true;

  const handleUsageSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['discount', id] });
  };
  
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      </div>
    );
  }
  
  if (isError || !discount) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="text-red-500 mt-0.5 mr-2 flex-shrink-0" size={20} />
          <div>
            <h3 className="font-medium text-red-800">Помилка завантаження</h3>
            <p className="text-red-700">Не вдалося завантажити деталі знижки. Спробуйте оновити сторінку.</p>
          </div>
        </div>
        <div className="mt-4">
          <Button to="/discounts" variant="outline" icon={<ArrowLeft size={16} />}>
            Повернутися до каталогу
          </Button>
        </div>
      </div>
    );
  }
  
  const isExpiringSoon = discount.endDate && 
    new Date(discount.endDate) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

  return (
    <div className="container mx-auto px-4 py-8 pb-20 md:pb-8">
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Лева колонка */}
        <div className="lg:w-2/3">
          <div className="mb-4">
            <Link 
              to="/discounts" 
              className="inline-flex items-center text-gray-600 hover:text-primary-600 mb-4"
            >
              <ArrowLeft size={16} className="mr-1" />
              Повернутися до каталогу
            </Link>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">{discount.title}</h1>
            <div className="flex items-center mb-4">
              <div className="flex items-center mr-4">
                <Store size={16} className="text-gray-500 mr-1" />
                <Link 
                  to={`/discounts?partnerId=${discount.partnerId}`}
                  className="text-primary-600 hover:text-primary-700"
                >
                  {discount.partnerName}
                </Link>
              </div>
              <div className="flex items-center">
                <Calendar size={16} className="text-gray-500 mr-1" />
                <span className="text-gray-600 text-sm">
                  {discount.startDate && 
                    `З ${format(new Date(discount.startDate), 'dd MMMM yyyy', { locale: uk })}`}
                  {discount.endDate && 
                    ` до ${format(new Date(discount.endDate), 'dd MMMM yyyy', { locale: uk })}`}
                </span>
              </div>
            </div>
          </div>
          
          {/* Изображение скидки */}
          <div className="mb-6">
            {discount.imageUrl ? (
              <img 
                src={discount.imageUrl} 
                alt={discount.title} 
                className="w-full h-auto rounded-lg shadow-sm"
              />
            ) : (
              <div className="w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center">
                <Tag size={48} className="text-gray-400" />
              </div>
            )}
          </div>
          
          {/* Описание скидки */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-3">Опис</h2>
            <RichTextDisplay
              content={discount.description}
              className="text-gray-700"
            />
          </div>

          {/* Условия использования */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-3">Умови використання</h2>
            <RichTextDisplay
              content={discount.terms}
              className="text-gray-700"
            />
          </div>

          {/* Как использовать */}
          <div className="mb-6">
            <h2 className="text-xl font-semibold mb-3">Як отримати знижку</h2>
            <RichTextDisplay
              content={discount.howToUse}
              className="text-gray-700"
            />
          </div>

          {/* Місце розташування */}
          {discount.location && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold mb-3">Місце розташування</h2>
              <RichTextDisplay
                content={discount.location}
                className="text-gray-700"
              />
            </div>
          )}
        </div>
        
        {/* Права колонка */}
        <div className="lg:w-1/3">
          {/* Карточка з інформацією о скидці */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-2">Інформація про знижку</h3>
              <div className="flex items-center">
                {discount.type === 'PERCENTAGE' && (
                  <div className="flex items-center mr-2">
                    <Percent size={20} className="text-blue-600 mr-1" />
                    <span className="text-2xl font-bold text-blue-600">{discount.percentageValue}%</span>
                  </div>
                )}
                {discount.type === 'FIXED' && (
                  <div className="flex items-center mr-2">
                    <CreditCard size={20} className="text-green-600 mr-1" />
                    <span className="text-2xl font-bold text-green-600">{discount.fixedValue} грн</span>
                  </div>
                )}
                {discount.type === 'SPECIAL_OFFER' && (
                  <div className="flex items-center mr-2">
                    <Tag size={20} className="text-purple-600 mr-1" />
                    <span className="text-2xl font-bold text-purple-600">Спеціальна пропозиція</span>
                  </div>
                )}
              </div>
              {(discount.priceRangeMin || discount.priceRangeMax) && (
                <div className="mt-2 text-sm text-gray-600">
                  Ціновий діапазон: {discount.priceRangeMin}–{discount.priceRangeMax} грн
                </div>
              )}
            </div>
            
            {isExpiringSoon && discount.endDate && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <div className="flex items-center text-amber-800">
                  <Clock size={18} className="text-amber-600 mr-2" />
                  <div>
                    <span className="font-medium">Скоро закінчиться!</span>
                    <p className="text-sm">
                      Знижка дійсна до {format(new Date(discount.endDate), 'dd MMMM yyyy', { locale: uk })}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="mb-4">
              <h4 className="font-medium text-gray-700 mb-2">Категорія:</h4>
              <Link 
                to={`/discounts?categoryId=${discount.categoryId}`}
                className="inline-flex items-center bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-700 hover:bg-gray-200"
              >
                {discount.categoryName}
              </Link>
            </div>
            
            <div className="mb-4">
              <h4 className="font-medium text-gray-700 mb-2">Використали:</h4>
              <div className="flex items-center">
                <Users size={16} className="text-gray-500 mr-1" />
                <span className="text-gray-600">{discount.usageCount} разів</span>
              </div>
            </div>

            {/* Кнопка для десктопу */}
            <div className="hidden md:block">
              {hasUsedDiscount ? (
                <div className="w-full bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <Check size={24} className="text-green-600 mx-auto mb-2" />
                  <p className="text-green-800 font-medium">Дякуємо, що скористалися знижкою!</p>
                </div>
              ) : (
                <Button
                  onClick={() => setShowUsageModal(true)}
                  className="w-full"
                >
                  Використати знижку
                </Button>
              )}
            </div>
          </div>
          
          {/* Информация о партнере */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center mb-4">
              {discount.partnerLogoUrl ? (
                <img 
                  src={discount.partnerLogoUrl} 
                  alt={discount.partnerName} 
                  className="w-12 h-12 rounded-full mr-3 object-cover border border-gray-200"
                />
              ) : (
                <div className="w-12 h-12 rounded-full mr-3 bg-gray-200 flex items-center justify-center">
                  <Store size={20} className="text-gray-400" />
                </div>
              )}
              <div>
                <h3 className="text-lg font-semibold">{discount.partnerName}</h3>
                <Link 
                  to={`/discounts?partnerId=${discount.partnerId}`}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  Всі знижки партнера
                </Link>
              </div>
            </div>
            
            <div className="space-y-3 text-gray-600">
              {discount.partner?.address && (
                <div className="flex items-start">
                  <MapPin size={16} className="text-gray-500 mr-2 mt-1 flex-shrink-0" />
                  <span>{discount.partner.address}</span>
                </div>
              )}
              
              {discount.partner?.workingHours && (
                <div className="flex items-start">
                  <Clock size={16} className="text-gray-500 mr-2 mt-1 flex-shrink-0" />
                  <span>{discount.partner.workingHours}</span>
                </div>
              )}
              
              {discount.partner?.contactPhone && (
                <div className="flex items-center">
                  <Phone size={16} className="text-gray-500 mr-2 flex-shrink-0" />
                  <a 
                    href={`tel:${discount.partner.contactPhone}`}
                    className="hover:text-primary-600"
                  >
                    {discount.partner.contactPhone}
                  </a>
                </div>
              )}
              
              {discount.partner?.contactEmail && (
                <div className="flex items-center">
                  <Mail size={16} className="text-gray-500 mr-2 flex-shrink-0" />
                  <a 
                    href={`mailto:${discount.partner.contactEmail}`}
                    className="hover:text-primary-600"
                  >
                    {discount.partner.contactEmail}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Закріплена кнопка для мобільних */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-40 shadow-lg">
        {hasUsedDiscount ? (
          <div className="w-full bg-green-50 border border-green-200 rounded-lg p-3 text-center">
            <p className="text-green-800 font-medium flex items-center justify-center">
              <Check size={18} className="mr-2" />
              Дякуємо, що скористалися знижкою!
            </p>
          </div>
        ) : (
          <Button
            onClick={() => setShowUsageModal(true)}
            className="w-full"
          >
            Використати знижку
          </Button>
        )}
      </div>
      
      {showUsageModal && (
        <DiscountUsageModal 
          discount={discount}
          onClose={() => setShowUsageModal(false)}
          onSuccess={handleUsageSuccess}
        />
      )}
    </div>
  );
};

export default DiscountDetails;
